import React, { useEffect, useState } from "react";
import { Plus, Trash2, Check, Users, Lock } from "lucide-react";
import { adminApi, useAdminAuth } from "../../lib/adminAuth";
import { Modal } from "./AdminCoupons";

export default function AdminUsers() {
  const { admin } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [pwModal, setPwModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => adminApi.listUsers().then(setUsers).catch(() => {});
  useEffect(load, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await adminApi.createUser({ email: editing.email, password: editing.password, name: editing.name || "Admin" });
      setEditing(null);
      load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Error creando admin");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar admin ${u.email}?`)) return;
    try {
      await adminApi.deleteUser(u.id);
      load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Error eliminando");
    }
  };

  return (
    <div data-testid="admin-users-page" className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">Cuentas</div>
          <h1 className="font-display text-4xl uppercase tracking-tight">Administradores</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">{users.length} admin(s). Cualquier admin puede crear/eliminar otros.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPwModal(true)} className="border-2 border-[#FEBA01] text-[#FEBA01] hover:bg-[#FEBA01] hover:text-black px-4 py-2 font-display uppercase inline-flex items-center gap-2" data-testid="admin-change-pw-btn">
            <Lock size={16} /> Mi clave
          </button>
          <button onClick={() => setEditing({ email: "", password: "", name: "" })} className="btn-brutal bg-[#FEBA01] text-black font-display uppercase px-4 py-2 inline-flex items-center gap-2 shadow-brutal-white" data-testid="admin-user-new-btn">
            <Plus size={16} /> Nuevo admin
          </button>
        </div>
      </header>

      {users.length === 0 ? (
        <div className="border-2 border-dashed border-[#333] p-10 text-center">
          <Users size={32} className="mx-auto text-[#FEBA01] mb-3" />
          <div className="font-display text-xl uppercase">Sin admins</div>
        </div>
      ) : (
        <div className="bg-[#1A1A1A] border-2 border-[#333] divide-y divide-[#333]">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`admin-user-${u.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display uppercase truncate">{u.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-0.5 truncate">
                  {u.email}{u.id === admin?.id && <span className="ml-2 text-[#FEBA01]">· tú</span>}
                </div>
              </div>
              {u.id !== admin?.id && (
                <button onClick={() => remove(u)} className="border-2 border-red-500 text-red-400 hover:bg-red-500 hover:text-white p-2" aria-label="Eliminar" data-testid={`admin-user-delete-${u.id}`}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title="Nuevo admin" testid="admin-user-modal">
          <form onSubmit={onSubmit} className="space-y-3">
            <Field label="Nombre">
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="inp" data-testid="admin-user-name" />
            </Field>
            <Field label="Email *">
              <input required type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="inp" data-testid="admin-user-email" />
            </Field>
            <Field label="Clave * (mín. 8)">
              <input required type="password" minLength={8} value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="inp" data-testid="admin-user-password" />
            </Field>
            {err && <div className="text-sm text-red-400">{err}</div>}
            <div className="flex gap-2 pt-2">
              <button type="submit" disabled={busy} className="btn-brutal bg-[#FEBA01] text-black font-display uppercase px-4 py-2 shadow-brutal-white inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-user-save">
                <Check size={16} /> {busy ? "Creando…" : "Crear admin"}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="border-2 border-[#333] text-[#A3A3A3] hover:text-white px-4 py-2 font-display uppercase">
                Cancelar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {pwModal && <ChangePasswordModal onClose={() => setPwModal(false)} />}

      <style>{`.inp { width: 100%; background:#0A0A0A; border:2px solid #333; padding: 0.5rem 0.75rem; outline:none; color:#F5F5F5; }
        .inp:focus { border-color: #FEBA01; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">{label}</span>
      {children}
    </label>
  );
}

function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await adminApi.changePassword(cur, next);
      setOk(true);
      setTimeout(onClose, 1200);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Error cambiando clave");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Cambiar mi clave" testid="admin-change-pw-modal">
      {ok ? (
        <div className="text-[#FEBA01] font-display text-lg uppercase">Clave actualizada ✓</div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <Field label="Clave actual">
            <input required type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="inp" data-testid="pw-current" />
          </Field>
          <Field label="Nueva clave (mín. 8)">
            <input required type="password" minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className="inp" data-testid="pw-new" />
          </Field>
          {err && <div className="text-sm text-red-400">{err}</div>}
          <button type="submit" disabled={busy} className="btn-brutal bg-[#FEBA01] text-black font-display uppercase px-4 py-2 shadow-brutal-white disabled:opacity-50" data-testid="pw-save">
            {busy ? "Guardando…" : "Cambiar clave"}
          </button>
        </form>
      )}
    </Modal>
  );
}
